import { useCallback, useMemo, useState } from 'react';
import axios from 'axios';
import _isEmpty from 'lodash/isEmpty';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';

import Table from 'components/customTable';
import {
  buildComboSalaryUrl,
  buildUrlUsingTitleAndLocation,
  yearlySalaryToHourlyRate,
} from 'utils';
import { HttpRoutes } from 'utils/constant';

import css from './index.module.css';

const AutosuggestInput = dynamic(() => import('components/autosuggestInput'), { ssr: false });

const columnsConfig = {
  rank: {
    columnName: 'Rank',
    defaultSortDirection: 'asc',
  },
  location: {
    columnName: 'City',
    isLink: 'comboSalaryPageIndexable',
    linkUrl: 'comboSalaryLink',
    linkTitle: 'locationColLinkTitle',
  },
  medianDisplay: {
    columnName: 'Avg. Salary',
    customSortByField: 'median',
  },
  hourlyRateDisplay: {
    columnName: 'Hourly Rate',
    customSortByField: 'hourlyRate',
  },
  jobsCount: {
    columnName: 'Job Count',
    linkUrl: 'jobCountLink',
    isLink: 'cityComboIndexable',
    customSortByField: 'jobsCount',
    linkTitle: 'jobCountColLinkTitle',
  },
};
export function SalaryByLocation({
  title,
  jobTitlePlural,
  tableSet = [],
  isResumeCountIndexable,
  indexablePopulation,
}) {
  const _tableSet = useMemo(() => {
    return tableSet.map((row) => {
      return {
        ...row,
        jobsCount: row.jobsCount > 0 ? row.jobsCount : '',
        jobCountLink: row.comboSalaryPageIndexable
          ? buildUrlUsingTitleAndLocation(title, row.location)
          : '',
        jobCountColLinkTitle: `${title} Jobs in ${row.location}`,
        locationColLinkTitle: `${title} Salary in ${row.location}`,
      };
    });
  }, [tableSet]);

  const [filteredTableData, setFilteredTableData] = useState(_tableSet);

  const onChange = useCallback(
    async (input) => {
      if (!_isEmpty(input.valueObj)) {
        const { data } = await axios.post(HttpRoutes.getAverageSalaryByCityAndTitle, {
          title,
          ...input.valueObj,
        });
        const { median } = data;

        const jobsCountData = await axios.post(HttpRoutes.getJobCountByLocation, {
          title,
          ...input.valueObj,
        });
        const jobsCountValue = jobsCountData?.data?.count || 0;

        const hourlyRate = yearlySalaryToHourlyRate(median);
        const comboSalaryLink = buildComboSalaryUrl(title, input.value);
        const comboSalaryPageIndexable =
          input.valueObj.city && isResumeCountIndexable && input.population >= indexablePopulation;

        setFilteredTableData([
          {
            location: input.displayValue,
            median,
            medianDisplay: `$${parseInt(median).toLocaleString()}`,
            hourlyRate,
            hourlyRateDisplay: `$${hourlyRate}`,
            comboSalaryLink,
            comboSalaryPageIndexable,
            rank: 1,
            locationColLinkTitle: `${title} Salary in ${input.displayValue}`,
            jobsCount: jobsCountValue > 0 ? jobsCountValue : '',
            jobCountLink: comboSalaryPageIndexable
              ? buildUrlUsingTitleAndLocation(title, input.value)
              : '',
            jobCountColLinkTitle: `${title} Jobs in ${input.displayValue}`,
          },
        ]);
      } else {
        setFilteredTableData(_tableSet);
      }
    },
    [title, tableSet]
  );

  return (
    <div className="w-730 mt-68">
      <h2 className="brandonH2 z-mb-48">{`Highest paying cities for ${jobTitlePlural?.toLowerCase?.()}`}</h2>
      <div className={css.input}>
        <AutosuggestInput
          category={'location'}
          inputProps={{
            className: 'zp-input zp-input-search mb-4',
            placeholder: 'Search For City',
          }}
          onChange={onChange}
        />
      </div>
      <Table
        columnConfig={columnsConfig}
        columnWidthBalanced={false}
        bodyData={filteredTableData}
        defaultRowsDisplay={filteredTableData.length}
      />
    </div>
  );
}

SalaryByLocation.propTypes = {
  title: PropTypes.string.isRequired,
  tableSet: PropTypes.array.isRequired,
  isResumeCountIndexable: PropTypes.bool.isRequired,
  indexablePopulation: PropTypes.number.isRequired,
};
